// The registry of per-user settings that follow the ACCOUNT, not the
// browser.
//
// Why this file exists
// ────────────────────
// Until 2026-08-15 every small user choice in the OS — email folder
// order, starred folders, nav pins, dark mode, the company/book scope
// lenses, per-page view choices — was written to localStorage. Each one
// looked defensible in isolation ("it's just a UI preference"), and each
// one silently reset when the advisor opened a different browser or a
// second computer. Grant hit it on the email folder list and asked for
// the whole class fixed rather than the one instance.
//
// So this is the checked-in answer to "what does the OS remember about
// me, and where does it live?" — one flat list, the same way
// shared/system-jobs/registry.ts answers "what runs by itself?".
//
// This module is PURE and imports nothing. That matters for two reasons:
//   * no import cycle (the provider imports this; domain modules import
//     descriptors from here and the hook from the provider), and
//   * it unit-tests without a DOM, React, or the api client.
//
// Storage shape
// ─────────────
// All settings live in ONE user_view_preferences row (surface_key
// "user_settings") as a flat JSON object keyed by setting id. Reads are
// a single fetch on app boot; writes merge one key into the blob and PUT
// the whole thing. See UserSettingsProvider for the caching + write
// path.
//
// Adding a setting
// ────────────────
// Add a descriptor below. If the setting previously lived in
// localStorage, set `legacyKey` + `parseLegacy` so the first load on
// each existing browser ADOPTS what's already there and pushes it up —
// nobody's setup resets to stock when their setting starts syncing.

/** Surface key of the single row holding every setting below. Pairs with
 *  "user_settings" in shared/db/view-preferences.ts KNOWN_SURFACE_KEYS. */
export const USER_SETTINGS_SURFACE_KEY = "user_settings";

/** The decoded blob: setting id → that setting's raw JSON value. Values
 *  are validated per-descriptor on read, never trusted from the wire. */
export type UserSettingsBlob = Record<string, unknown>;

export interface UserSettingDescriptor<T> {
  /** Stable key inside the blob. Dotted by domain ("email.folders") so
   *  the stored object stays readable in the admin preference viewer. */
  id: string;
  /** Value used when neither the account nor a legacy browser copy has
   *  anything. Must be a fresh value per call — never a shared mutable
   *  object, since consumers hold it in React state. */
  baseline: () => T;
  /** Decode this setting's slot out of the blob. Returns null for
   *  missing/malformed, which falls back to baseline. Tolerant by
   *  design: a shape change must degrade to the default, never throw
   *  and blank the screen. */
  parse: (raw: unknown) => T | null;
  /** Encode for storage. Must round-trip through JSON.stringify. */
  serialize: (value: T) => unknown;
  /** localStorage key this setting used to live under, if any. Present
   *  only for settings migrated off per-browser storage; a setting born
   *  account-level has no legacy key. */
  legacyKey?: string;
  /** Decode the legacy localStorage payload. Same tolerance as parse. */
  parseLegacy?: (raw: string) => T | null;
  /** Escape hatch for a setting whose old storage wasn't ONE key.
   *  Book scope is the case: it wrote one key per list entity type
   *  (`stewardshipos.list.contacts.book-scope`, `…households…`, …), so
   *  adoption has to scan rather than read a known key. Takes precedence
   *  over legacyKey/parseLegacy when present; returns null to adopt
   *  nothing. */
  adoptLegacy?: (read: (key: string) => string | null, listKeys: () => string[]) => T | null;
}

// ─── Shared decode helpers ────────────────────────────────────────────

function asStringArray(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out = raw.filter((s): s is string => typeof s === "string");
  // De-dupe on read as well as write: a blob hand-edited by an admin, or
  // written by an older build, shouldn't be able to produce a folder
  // rendered twice.
  return Array.from(new Set(out));
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ─── Descriptor factories ─────────────────────────────────────────────
//
// Most settings are "one of N strings" or "on / off". Spelling each out
// longhand buried the interesting ones (folder prefs, book scope) in
// boilerplate, so the ordinary cases get a factory and the unusual ones
// stay hand-written.

/** A setting whose value is one of a fixed set of strings. Anything not
 *  in the set — on the account or in the legacy key — is treated as no
 *  value at all, which falls back to the baseline. */
function enumSetting<T extends string>(opts: {
  id: string;
  values: readonly T[];
  baseline: T;
  legacyKey: string;
}): UserSettingDescriptor<T> {
  const isValid = (raw: unknown): raw is T =>
    typeof raw === "string" && (opts.values as readonly string[]).includes(raw);
  return {
    id: opts.id,
    baseline: () => opts.baseline,
    parse: (raw) => (isValid(raw) ? raw : null),
    serialize: (value) => value,
    legacyKey: opts.legacyKey,
    parseLegacy: (raw) => (isValid(raw) ? raw : null),
  };
}

/** An on/off setting. The legacy keys stored "1"/"0" strings; anything
 *  else was never a deliberate choice, so it adopts nothing. */
function booleanSetting(opts: {
  id: string;
  baseline: boolean;
  legacyKey: string;
}): UserSettingDescriptor<boolean> {
  return {
    id: opts.id,
    baseline: () => opts.baseline,
    parse: (raw) => (typeof raw === "boolean" ? raw : null),
    serialize: (value) => value,
    legacyKey: opts.legacyKey,
    parseLegacy: (raw) => (raw === "1" ? true : raw === "0" ? false : null),
  };
}

// ─── Email: folder order + hidden folders ─────────────────────────────

/** The "Customize folders" dialog's two controls. `hidden` is a Set at
 *  the consumer boundary (membership tests on every render) but stores
 *  as an array — JSON has no set type. */
export interface EmailFolderPrefs {
  hidden: Set<string>;
  order: string[];
}

interface SerializedFolderPrefs {
  hidden?: unknown;
  order?: unknown;
}

function decodeFolderPrefs(raw: unknown): EmailFolderPrefs | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const o = raw as SerializedFolderPrefs;
  const hidden = asStringArray(o.hidden) ?? [];
  const order = asStringArray(o.order) ?? [];
  return { hidden: new Set(hidden), order };
}

export const EMAIL_FOLDER_PREFS: UserSettingDescriptor<EmailFolderPrefs> = {
  id: "email.folders",
  baseline: () => ({ hidden: new Set<string>(), order: [] }),
  parse: decodeFolderPrefs,
  serialize: (value) => ({
    hidden: Array.from(value.hidden),
    order: value.order,
  }),
  legacyKey: "stewardship-os.email.folder-prefs.v1",
  parseLegacy: (raw) => decodeFolderPrefs(parseJson(raw)),
};

// ─── Email: starred folders (the FAVORITES group) ─────────────────────

// Matches Outlook desktop's out-of-the-box pinning. Returned fresh each
// call so a consumer mutating its copy can't poison the default.
export const DEFAULT_EMAIL_FAVORITES: ReadonlyArray<string> = ["inbox", "sentitems"];

export const EMAIL_FAVORITES: UserSettingDescriptor<Set<string>> = {
  id: "email.favorites",
  baseline: () => new Set(DEFAULT_EMAIL_FAVORITES),
  parse: (raw) => {
    const ids = asStringArray(raw);
    return ids === null ? null : new Set(ids);
  },
  serialize: (value) => Array.from(value),
  legacyKey: "stewardship-os.email.favorites.v1",
  parseLegacy: (raw) => {
    const ids = asStringArray(parseJson(raw));
    return ids === null ? null : new Set(ids);
  },
};

// ─── Navigation: pinned sidebar items ─────────────────────────────────

/** One entry in the sidebar's "Pinned" section. Typed structurally here
 *  rather than importing PinnedItem from components/nav-pins, because
 *  that would pull the sidebar's IconName union — and this module's
 *  no-imports rule is what keeps it testable without React. nav-pins
 *  narrows `icon` back to IconName at its own boundary. */
export interface PinnedNavItem {
  label: string;
  to: string;
  icon: string;
}

/** Matches MAX_PINS in components/nav-pins.ts. Enforced on both read and
 *  write so a hand-edited or older-build blob can't render a 40-item
 *  sidebar. */
export const MAX_NAV_PINS = 7;

function decodeNavPins(raw: unknown): PinnedNavItem[] | null {
  if (!Array.isArray(raw)) return null;
  const out: PinnedNavItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const p = entry as Partial<PinnedNavItem>;
    if (typeof p.label !== "string" || typeof p.to !== "string" || typeof p.icon !== "string") {
      continue;
    }
    if (seen.has(p.to)) continue; // dedupe by destination, as addPin does
    seen.add(p.to);
    out.push({ label: p.label, to: p.to, icon: p.icon });
  }
  return out.slice(0, MAX_NAV_PINS);
}

export const NAV_PINS: UserSettingDescriptor<PinnedNavItem[]> = {
  id: "nav.pins",
  baseline: () => [],
  parse: decodeNavPins,
  serialize: (value) => value.slice(0, MAX_NAV_PINS),
  legacyKey: "stewardshipos.nav.pins",
  parseLegacy: (raw) => decodeNavPins(parseJson(raw)),
};

// ─── Navigation: collapsed sidebar section headings ───────────────────

// Which sidebar GROUPS the user has folded (keyed by heading text, e.g.
// "ADMIN"). Distinct from whether the whole rail is collapsed — that one
// tracks screen width and stays per-device.
export const NAV_COLLAPSED_SECTIONS: UserSettingDescriptor<Set<string>> = {
  id: "nav.collapsedSections",
  baseline: () => new Set<string>(),
  parse: (raw) => {
    const ids = asStringArray(raw);
    return ids === null ? null : new Set(ids);
  },
  serialize: (value) => Array.from(value),
  legacyKey: "stewardshipos.sidebar.collapsedSections",
  parseLegacy: (raw) => {
    const ids = asStringArray(parseJson(raw));
    return ids === null ? null : new Set(ids);
  },
};

// ─── Navigation: how the second pane opens ────────────────────────────
//
// The rail is two panes as of 2026-08-23 (Grant): a fixed 56px column of
// heading icons that never moves, and a second pane to its right
// holding the selected heading's destinations. That structure is what
// made "start collapsed" coherent — before it, "pane collapsed" and
// "sections collapsed" were two states fighting each other, and
// collapsing the rail produced a column of twenty-odd unlabeled icons
// rather than something simpler.
//
// So there is ONE control instead of two:
//   "closed" — the second pane starts shut. The rail icons, nothing
//              else. The shipped default, per Grant's ask.
//   "last"   — it starts open on whichever section you used last.
export type NavSecondPaneMode = "closed" | "last";

export const NAV_SECOND_PANE_MODE: UserSettingDescriptor<NavSecondPaneMode> = {
  id: "nav.secondPaneMode",
  baseline: () => "closed",
  parse: (raw) => (raw === "closed" || raw === "last" ? raw : null),
  serialize: (value) => value,
  // Born account-level: there is no per-browser predecessor to adopt.
  // The old per-device key ("stewardshipos.sidebar.collapsed") is
  // deliberately NOT read here — it recorded a momentary toggle, not a
  // considered preference, and half the team's copy of it was set by the
  // narrow-window auto-collapse rather than by a person.
};

// The SECOND control from Grant's 2026-08-23 ask: "the options below to
// be open or collapsed by default."
//
// The two-pane rail answered most of that structurally — a section's
// destinations are shown when its pane is open and hidden when it is
// shut, so there is no separate state to set for the flat sections.
// Admin is the exception and the reason this setting exists: it holds
// seven labeled GROUPS (People, Data, Compliance, Commissions, Config,
// System, Advanced) that fold independently, and those genuinely are
// "the options below" with their own default worth choosing.
//
//   "collapsed" — every group starts folded. The shipped default, per
//                 the same message. Opening one is a click and does not
//                 stick between sessions.
//   "open"      — every group starts unfolded.
//   "remember"  — whatever you left folded stays folded, on every
//                 machine (this is what NAV_COLLAPSED_SECTIONS stores).
export type NavGroupsMode = "collapsed" | "open" | "remember";

export const NAV_ADMIN_GROUPS_MODE: UserSettingDescriptor<NavGroupsMode> = {
  id: "nav.adminGroupsMode",
  baseline: () => "collapsed",
  parse: (raw) => (raw === "collapsed" || raw === "open" || raw === "remember" ? raw : null),
  serialize: (value) => value,
};

// Which section the second pane was showing when you last used the app.
// Only consulted when NAV_SECOND_PANE_MODE is "last". Stored as a plain
// string rather than the SectionKey union: a key can disappear between
// releases (a section is renamed, or the user's role changes and Admin
// is no longer theirs), and the rail treats an unrecognized value as
// "no section" rather than trusting it.
export const NAV_LAST_SECTION: UserSettingDescriptor<string | null> = {
  id: "nav.lastSection",
  baseline: () => null,
  parse: (raw) => (typeof raw === "string" && raw.length > 0 ? raw : null),
  serialize: (value) => value,
};

// ─── Appearance: light / dark theme ───────────────────────────────────

// Dark is opt-in and never inferred from the OS setting (Grant,
// 2026-07-23 — auto-serving dark to first-time users on dark-mode Macs
// produced Jake's washed-out first run).
//
// This one keeps writing its legacy localStorage key as well as the
// account, and deliberately so: index.html runs a pre-hydration script
// that reads `stewardshipos.darkMode` to paint the theme BEFORE React
// mounts. Without a synchronous local copy the app would flash light and
// then repaint dark on every load for dark-mode users. The account row
// is the preference; the local key is a paint cache, which is exactly
// the carve-out the CLAUDE.md storage rule allows.
export const APPEARANCE_DARK_MODE: UserSettingDescriptor<boolean> = {
  id: "appearance.darkMode",
  baseline: () => false,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
  legacyKey: "stewardshipos.darkMode",
  // The legacy key stores "1" / "0". Anything else — absent, garbage —
  // is not a deliberate choice, so it adopts nothing and stays light.
  parseLegacy: (raw) => (raw === "1" ? true : raw === "0" ? false : null),
};

// ─── Scope lenses: "default to your desk" ─────────────────────────────
//
// These three decide what a list OPENS on — the company lens, the
// My book / Everyone toggle, and Cleanup's Mine / Everyone. They are the
// highest-stakes settings in this program: CLAUDE.md's "default to your
// desk" doctrine governs what every user sees first, and a wrong default
// is either a firehose (the thing the doctrine exists to prevent) or a
// list that looks empty because it silently narrowed.
//
// Moving them to the account only changes WHERE the user's choice is
// remembered. The resolution rules — URL param wins, then the saved
// choice, then the role default — are untouched and still live in
// company-context.ts / book-scope.ts / cleanup-owner-scope.ts.

/** A company slug, or one of the sentinels COMPANY_CONTEXT_ALL /
 *  COMPANY_CONTEXT_MINE. null means "no choice yet" — which is NOT the
 *  same as "all", and the distinction matters: a multi-company user's
 *  deliberate "All companies" must not be re-narrowed on the next load. */
export const SCOPE_COMPANY: UserSettingDescriptor<string | null> = {
  id: "scope.company",
  baseline: () => null,
  parse: (raw) => (typeof raw === "string" && raw.length > 0 ? raw : null),
  serialize: (value) => value,
  legacyKey: "stewardshipos.list.company-context",
  parseLegacy: (raw) => (raw.length > 0 ? raw : null),
};

/** Per-list-entity My book / Everyone choice, e.g. { contacts: "all" }.
 *  A missing entry means "no explicit choice", which the resolver reads
 *  as the role default — so this must stay sparse rather than filling in
 *  "own" for every entity the user has ever visited. */
export type BookScopeChoices = Record<string, "own" | "all">;

// The legacy key shape: `stewardshipos.list.<entityType>.book-scope`.
const BOOK_SCOPE_LEGACY_PREFIX = "stewardshipos.list.";
const BOOK_SCOPE_LEGACY_SUFFIX = ".book-scope";

function decodeBookScopes(raw: unknown): BookScopeChoices | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const out: BookScopeChoices = {};
  for (const [entity, choice] of Object.entries(raw as Record<string, unknown>)) {
    if (choice === "own" || choice === "all") out[entity] = choice;
  }
  return out;
}

export const SCOPE_BOOK: UserSettingDescriptor<BookScopeChoices> = {
  id: "scope.book",
  baseline: () => ({}),
  parse: decodeBookScopes,
  serialize: (value) => value,
  // No single legacyKey — this wrote one key per entity type, so
  // adoption scans for the whole family.
  adoptLegacy: (read, listKeys) => {
    const out: BookScopeChoices = {};
    for (const key of listKeys()) {
      if (!key.startsWith(BOOK_SCOPE_LEGACY_PREFIX) || !key.endsWith(BOOK_SCOPE_LEGACY_SUFFIX)) {
        continue;
      }
      const entity = key.slice(
        BOOK_SCOPE_LEGACY_PREFIX.length,
        key.length - BOOK_SCOPE_LEGACY_SUFFIX.length,
      );
      if (!entity) continue;
      const value = read(key);
      if (value === "own" || value === "all") out[entity] = value;
    }
    // Adopt nothing (rather than an empty object) when this browser had
    // no choices — writing {} would claim an answer the user never gave.
    return Object.keys(out).length > 0 ? out : null;
  },
};

/**
 * The person's own answer to "when I open a list or board, should it
 * start on my book or on everyone's work?" (Jake Norton, 2026-09-12,
 * relayed by Grant: "allow users to choose what their default view is
 * with no click.")
 *
 * This is a THIRD tier, sitting between the two that already existed:
 * a per-list/per-surface choice in SCOPE_BOOK still wins when one is
 * set (someone who wants Everyone on Processing specifically but My
 * book everywhere else keeps that); failing that, THIS setting answers
 * for every book-scoped surface at once; failing that, the role default
 * (defaultsToOwnBook) answers, exactly as before this setting existed.
 *
 * null = no explicit choice, same convention as SCOPE_CLEANUP_OWNER /
 * SCOPE_FORMS above — falls through to the next tier down. Born
 * account-level; no legacyKey, because there was never a single global
 * switch to adopt (only the per-list ones SCOPE_BOOK already owns).
 */
export const SCOPE_BOOK_DEFAULT: UserSettingDescriptor<"own" | "all" | null> = {
  id: "scope.bookDefault",
  baseline: () => null,
  parse: (raw) => (raw === "own" || raw === "all" ? raw : null),
  serialize: (value) => value,
};

/** Cleanup workspace's OWNER toggle. null = no explicit choice, which
 *  falls through to the role default (Everyone for system_admin /
 *  compliance / front_desk, Mine for everyone else). */
export const SCOPE_CLEANUP_OWNER: UserSettingDescriptor<"mine" | "everyone" | null> = {
  id: "scope.cleanupOwner",
  baseline: () => null,
  parse: (raw) => (raw === "mine" || raw === "everyone" ? raw : null),
  serialize: (value) => value,
  legacyKey: "cleanup.owner-scope",
  parseLegacy: (raw) => (raw === "mine" || raw === "everyone" ? raw : null),
};

/** Forms page's dual-level ownership toggle (mine / company / all).
 *  null = no explicit choice, which falls through to the role default —
 *  see web/src/utils/forms-scope.ts. Born account-level; no legacyKey. */
export const SCOPE_FORMS: UserSettingDescriptor<"mine" | "company" | "all" | null> = {
  id: "scope.forms",
  baseline: () => null,
  parse: (raw) => (raw === "mine" || raw === "company" || raw === "all" ? raw : null),
  serialize: (value) => value,
};

// ─── Per-page view choices ────────────────────────────────────────────
//
// The long tail: which tab a page opens on, how dense a board is, which
// of two layouts someone prefers. Individually trivial — collectively
// they are most of what "the OS remembers me" means day to day, and
// every one of them used to reset on a second machine.

/** Tasks page: "Stewardship" (OS tasks) vs "All To Do" (with Microsoft
 *  To Do merged in). Also written by the dashboard's "Tasks due" tile,
 *  which preselects the tab its number counted. */
export const TASKS_PREFERRED_TAB = enumSetting({
  id: "tasks.preferredTab",
  values: ["stewardship", "all_todo"] as const,
  baseline: "stewardship",
  legacyKey: "stewardship-os.tasks.preferred-tab.v1",
});

/** Ongoing Care: which of the five views the user last chose. Andrea
 *  works from the renewal board; Joy from the calendar. A bare
 *  /ongoing-care restores this; an explicit ?view= deep link always
 *  wins. null = never chosen, page uses its calendar default. */
export type OngoingCareView =
  | "calendar"
  | "timeline"
  | "workload"
  | "table"
  | "board";
const ONGOING_CARE_VIEWS: readonly OngoingCareView[] = [
  "calendar",
  "timeline",
  "workload",
  "table",
  "board",
];
// Hand-rolled rather than enumSetting(): that helper requires a
// legacyKey, and this setting is born on the account — there is no
// localStorage past to adopt.
export const ONGOING_CARE_VIEW: UserSettingDescriptor<OngoingCareView | null> = {
  id: "ongoingCare.view",
  baseline: () => null,
  parse: (raw) =>
    typeof raw === "string" && (ONGOING_CARE_VIEWS as readonly string[]).includes(raw)
      ? (raw as OngoingCareView)
      : null,
  serialize: (value) => value,
};

/** Ongoing Care: where a click on a board tile / table row lands.
 *  "case" opens the linked case itself (the Medicare case, the policy);
 *  "plan" opens the household-level care plan page, as before. A plan
 *  with no linked case always opens the plan page, whatever this says.
 *
 *  null = never chosen, so the role default applies (see
 *  resolveCareOpenTarget in components/ongoing-care/care-open-target.ts:
 *  Planning-only users default to "case"). Stored as null-until-chosen
 *  rather than baked into baseline() because this module is pure and
 *  cannot know the viewer's company scope. Andrea, 2026-10-09: the plan
 *  page costs her a click through the household on every Medicare card. */
export type CareOpenTarget = "case" | "plan";
export const ONGOING_CARE_OPEN_TARGET: UserSettingDescriptor<CareOpenTarget | null> = {
  id: "ongoingCare.openTarget",
  baseline: () => null,
  parse: (raw) => (raw === "case" || raw === "plan" ? raw : null),
  serialize: (value) => value,
};

/** Ongoing Care: the filters the user last worked under (companies,
 *  plan types, products, statuses, owners — never the free-text
 *  search, which is transient). A bare /ongoing-care restores these so
 *  Andrea lands back on "board · Medicare" rather than re-picking it
 *  every visit; changing filters on screen updates it in place.
 *
 *  null (not empty arrays) when the user last CLEARED everything:
 *  clearing is a transient widening to peek firm-wide, and the next
 *  visit should return to their desk (the company seed), not to the
 *  firehose they peeked at. */
export interface OngoingCareSavedFilters {
  companies: string[];
  types: string[];
  products: string[];
  statuses: string[];
  owners: string[];
}
function decodeSavedCareFilters(raw: unknown): OngoingCareSavedFilters | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const arr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const out: OngoingCareSavedFilters = {
    companies: arr(r.companies),
    types: arr(r.types),
    products: arr(r.products),
    statuses: arr(r.statuses),
    owners: arr(r.owners),
  };
  // All-empty stored shape decodes to null — same meaning as unset.
  return Object.values(out).some((a) => a.length > 0) ? out : null;
}
export const ONGOING_CARE_FILTERS: UserSettingDescriptor<OngoingCareSavedFilters | null> = {
  id: "ongoingCare.filters",
  baseline: () => null,
  parse: decodeSavedCareFilters,
  serialize: (value) => value,
};

/** Marketing Today: the filters and sort the person saved with "Save this
 *  view" (Marketing Hub row 3.0). Today opens on it, "Reset to the standard
 *  view" clears it. Account-level, not browser-level: the localStorage rule
 *  asks "would they be annoyed to re-pick this on another computer?", and a
 *  Today view someone built is exactly that.
 *
 *  DECISION: one saved view per person, as the drawing shows one "Save this
 *  view" link; named views are not drawn, so they are not built. The desk
 *  and book lenses are NOT in it: the working-scope control already saves
 *  those (SCOPE_COMPANY / SCOPE_BOOK). Values are plain strings; the server
 *  re-validates every filter on each request, so a stale product or a hand
 *  edit degrades to "no filter", never to an error. null = the standard
 *  view. */
export interface MarketingTodayView {
  chips: {
    product: string;
    band: string;
    urgency: string;
    moment: string;
    advisor: string;
    goes_away_days: string;
    status: string;
  };
  sort: string;
}
function decodeMarketingTodayView(raw: unknown): MarketingTodayView | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as { chips?: unknown; sort?: unknown };
  if (r.chips === null || typeof r.chips !== "object" || Array.isArray(r.chips)) return null;
  const c = r.chips as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  return {
    chips: {
      product: str(c.product),
      band: str(c.band),
      urgency: str(c.urgency),
      moment: str(c.moment),
      advisor: str(c.advisor),
      goes_away_days: str(c.goes_away_days),
      status: str(c.status),
    },
    sort: str(r.sort),
  };
}
export const MARKETING_TODAY_VIEW: UserSettingDescriptor<MarketingTodayView | null> = {
  id: "marketing.todayView",
  baseline: () => null,
  parse: decodeMarketingTodayView,
  serialize: (value) => value,
};

/** The marketing Calendar (Marketing Hub row 8.4): the view and the two
 *  filters, saved to the account so the same choice holds on a phone
 *  (spec 13: "persisted per user"). null companies = every company the
 *  viewer reads. Born on the account: no browser past to adopt. */
export interface MarketingCalendarView {
  view: "month" | "week" | "list";
  companies: string[] | null;
  status: "all" | "waiting" | "scheduled" | "sent" | "failed_held";
}
const MARKETING_CALENDAR_VIEWS = ["month", "week", "list"] as const;
const MARKETING_CALENDAR_STATUSES = ["all", "waiting", "scheduled", "sent", "failed_held"] as const;
function decodeMarketingCalendarView(raw: unknown): MarketingCalendarView | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const view = typeof r.view === "string" && (MARKETING_CALENDAR_VIEWS as readonly string[]).includes(r.view) ? (r.view as MarketingCalendarView["view"]) : "month";
  const status = typeof r.status === "string" && (MARKETING_CALENDAR_STATUSES as readonly string[]).includes(r.status) ? (r.status as MarketingCalendarView["status"]) : "all";
  const companies = Array.isArray(r.companies) ? r.companies.filter((c): c is string => typeof c === "string") : null;
  return { view, status, companies: companies && companies.length > 0 ? companies : null };
}
export const MARKETING_CALENDAR_VIEW: UserSettingDescriptor<MarketingCalendarView | null> = {
  id: "marketing.calendarView",
  baseline: () => null,
  parse: decodeMarketingCalendarView,
  serialize: (value) => value,
};

/** Calendar: Nate asked for Week to stay Week instead of resetting to
 *  Agenda on every visit — which it now does across machines too. */
export const CALENDAR_VIEW = enumSetting({
  id: "calendar.view",
  values: ["agenda", "week", "month"] as const,
  baseline: "agenda",
  legacyKey: "cal:view",
});

// Donna's front-desk dashboard. Four separate choices, all hers.
export const DONNA_SCHEDULE_VIEW = enumSetting({
  id: "donna.scheduleView",
  values: ["clients", "all"] as const,
  baseline: "all",
  legacyKey: "donna.schedule.view.v2",
});
export const DONNA_SCHEDULE_GROUP = enumSetting({
  id: "donna.scheduleGroup",
  values: ["advisor", "time"] as const,
  baseline: "advisor",
  legacyKey: "donna.schedule.group.v2",
});
// Which way the front-desk board is turned: advisors as columns with
// time running down ("down", the board as it has always been) or
// advisors as rows with time running across ("across", the Outlook
// Schedule View way). Donna, 2026-09-30: "my brain works better off of
// what I have been used to." Born per-person: choosing it changes nobody
// else's board. The legacy key is never written; it exists only because
// the helper wants one.
export const DONNA_BOARD_LAYOUT = enumSetting({
  id: "donna.boardLayout",
  values: ["down", "across"] as const,
  baseline: "down",
  legacyKey: "donna.schedule.layout.v1",
});
// Day or Week on the front desk board (Donna, 2026-10-05: "the week view
// is very useful. I don't need to have a full month"). Per-person.
export const DONNA_BOARD_RANGE = enumSetting({
  id: "donna.boardRange",
  values: ["day", "week"] as const,
  baseline: "day",
  legacyKey: "donna.schedule.range.v1",
});
export const DONNA_EXTRA_ADVISORS = booleanSetting({
  id: "donna.extraAdvisors",
  baseline: false,
  legacyKey: "donna.schedule.extraAdvisors",
});
// Which advisors get a permanent column on her board, by user id.
// `null` (the baseline — nothing saved yet) means "not customized": the
// board falls back to Grant's pinned eight, exactly as before. Once she
// checks or unchecks anyone in the calendar picker this becomes an
// explicit list (2026-08-18, Donna: "can I add or view a calendar...
// or view someone's and then put it away"). Born account-level — there
// is no prior localStorage key to adopt.
export const DONNA_BOARD_MEMBER_IDS: UserSettingDescriptor<string[] | null> = {
  id: "donna.boardMemberIds",
  baseline: () => null,
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  serialize: (value) => value,
};
// Off by default for everyone (Donna, 2026-08-04: "I don't need the
// voice brief on my dashboard") — whoever does want it turns it on.
export const DONNA_VOICE_BRIEF = booleanSetting({
  id: "donna.voiceBrief",
  baseline: false,
  legacyKey: "donna.dashboard.showVoiceBrief",
});

// Whether the "Automations across all stages" panel on a kanban board
// (Processing, Opportunities) shows its rule sentences expanded or
// collapsed to one summary line. Collapsed by default (Brandon,
// 2026-08-21: the board itself should be what fills the screen, not the
// automations writeup above it) — the panel is one click away either
// way, and remembering the choice per account means opening it on a
// laptop keeps it open on a tablet.
export const BOARD_AUTOMATIONS_OPEN = booleanSetting({
  id: "board.automationsOpen",
  baseline: false,
  legacyKey: "stewardship-os.board.automations-open.v1",
});

// Page densities. Note these are per-PAGE and separate from the
// list-view density, which already syncs through its own surface key.
export const DENSITY_MY_DAY = enumSetting({
  id: "density.myDay",
  values: ["tiles", "list"] as const,
  baseline: "tiles",
  legacyKey: "stewardshipos.my-day.density",
});
// DECISION: the Processing board's "Compact" chip retired with the one-row
// toolbar (Brandon, 2026-09-29), so nothing reads this today. It stays
// registered so people's saved choice survives if the toggle comes back.
export const DENSITY_PROCESSING_BOARD = enumSetting({
  id: "density.processingBoard",
  values: ["comfortable", "compact"] as const,
  baseline: "comfortable",
  legacyKey: "processing-board-density",
});
export const DENSITY_ADMIN_TEAM = enumSetting({
  id: "density.adminTeam",
  values: ["comfortable", "compact"] as const,
  baseline: "comfortable",
  legacyKey: "admin-team:density",
});

/** The briefing dashboard's "more" disclosure. */
export const BRIEFING_MORE_OPEN = booleanSetting({
  id: "briefing.moreOpen",
  baseline: false,
  legacyKey: "briefing-more-open-v1",
});

/** Processing record hero (BlockingStack) collapse state — Nate Swartz's
 *  ask, 2026-08-24: the stage / next-move / blocker hero repeats data
 *  shown elsewhere on the page (the stage strip, the Work tab's
 *  Conditions tracker), and he wanted it out of his way. `null` means
 *  "no explicit choice yet" — distinct from an explicit `false` —
 *  because the SMART DEFAULT differs by person: Grant asked for it
 *  collapsed by default for Nate specifically (2026-08-24), not for the
 *  rest of the team, so the consuming component resolves `null` against
 *  the viewer's identity. Once someone clicks the toggle themselves,
 *  that explicit choice always wins over the smart default. */
/** Ask Stew v3 PR 4 — the inline "Stew brief" band on household and record
 *  pages. Default on for every role (Grant approved the mockup's default-on,
 *  hideable band, 2026-09-23); "Hide" here is an ACCOUNT setting, because a
 *  person who hid it on one computer would be annoyed to hide it again on
 *  the next. `true` = hidden. The Cleanup button stays fixed chrome; this
 *  band is not a safety affordance, so it may be hidden. */
export const STEW_BRIEF_BAND_HIDDEN: UserSettingDescriptor<boolean> = {
  id: "stew.briefBandHidden",
  baseline: () => false,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};

export const PROC_HERO_COLLAPSED: UserSettingDescriptor<boolean | null> = {
  id: "processing.heroCollapsed",
  baseline: () => null,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};

/** "Conditions to clear" + "Compliance readiness" cards on the Processing
 *  record Work tab — Nate Swartz's ask, 2026-08-25: the two cards repeat
 *  the same checklist the hero already surfaces (open/blocked items with
 *  one-click resolve buttons), and he wanted them out of his way. This is
 *  scoped to Nate specifically, not a shared default: Mylah, Cambria,
 *  Ryan, and Brian Baker all work directly out of these cards per the
 *  help articles that document them, so hiding them by default for
 *  everyone would take away a workspace other teammates rely on. Same
 *  `null` = "hasn't chosen, resolve a smart per-viewer default" shape as
 *  PROC_HERO_COLLAPSED above — an explicit toggle always wins over the
 *  smart default once someone sets one. */
export const PROC_CONDITIONS_COMPLIANCE_HIDDEN: UserSettingDescriptor<boolean | null> = {
  id: "processing.conditionsComplianceHidden",
  baseline: () => null,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};

/** Which tile occupies the one shared slot in the traditional-mortgage
 *  Processing "loan summary" grid: seller+lender **Total credits**
 *  (everyone's default, unchanged) or **First payment date** (Cambria's
 *  ask, 2026-09-02 — she reads it off the Note, not the Closing
 *  Disclosure). This is a PERSONAL preference, not a shared default —
 *  Grant's explicit call: "I only want this change for her." Same `null`
 *  = "hasn't chosen, resolve a smart per-viewer default" shape as
 *  PROC_CONDITIONS_COMPLIANCE_HIDDEN above: unset resolves to
 *  "first_payment_date" only when the viewer is Cambria, "total_credits"
 *  for everyone else — and an explicit choice (from the grid's own
 *  toggle link) always wins over that smart default, so this stays a
 *  real reversible preference rather than a one-way switch. */
export type LoanSummaryCreditsSlot = "total_credits" | "first_payment_date" | null;
export const PROC_LOAN_SUMMARY_CREDITS_SLOT: UserSettingDescriptor<LoanSummaryCreditsSlot> = {
  id: "processing.loanSummaryCreditsSlot",
  baseline: () => null,
  parse: (raw) => (raw === "total_credits" || raw === "first_payment_date" ? raw : null),
  serialize: (value) => value,
};

/** Where the "Notes & meetings" feed sits on the Processing record page:
 *  **top** (everyone's default, unchanged — above the tabbed workspace,
 *  same placement it's had since it opened to every role 2026-09-17),
 *  **bottom** (below the tabbed workspace and Key Dates), or **people**
 *  (inside the People & notes tab, past the "Customize view" button —
 *  Nate Swartz's ask, 2026-09-21: on a Realty file with a long notes
 *  history, "bottom" still means always on screen above the Work tabs,
 *  and he wanted it out of the way entirely until he opens that tab).
 *  Joey Ellis's original ask, 2026-09-19, was top/bottom only; this
 *  is a PERSONAL preference, not a shared default — the feed's whole
 *  point is being visible "without an extra click," so moving it for
 *  every advisor would undo that for people who rely on the current
 *  placement (Brian Ream, Ryan — see NotesMeetingsFeed's own history).
 *  Same `null` = "hasn't chosen, resolve a smart per-viewer default"
 *  shape as PROC_LOAN_SUMMARY_CREDITS_SLOT above: unset resolves to
 *  "bottom" only when the viewer is Joey, "top" for everyone else —
 *  and an explicit choice (from the feed's own toggle link) always
 *  wins over that smart default. */
export type NotesMeetingsPosition = "top" | "bottom" | "people" | null;
export const PROC_NOTES_MEETINGS_POSITION: UserSettingDescriptor<NotesMeetingsPosition> = {
  id: "processing.notesMeetingsPosition",
  baseline: () => null,
  parse: (raw) =>
    raw === "top" || raw === "bottom" || raw === "people" ? raw : null,
  serialize: (value) => value,
};

/** Whether a "Latest notes" section sits at the bottom of a case / policy
 *  page — the ActiveCampaign habit of scrolling down to read the notes
 *  (Andrea, 2026-10-12: "in active campaign she could just scroll to the
 *  bottom of the page and see all the notes").
 *
 *  A PERSONAL preference, not a page-wide move: a Realty or Mortgage
 *  advisor working a file does not want a notes strip under it. `null` =
 *  "hasn't chosen, resolve a smart per-viewer default" (same shape as
 *  PROC_NOTES_MEETINGS_POSITION): on for viewers whose company scope is
 *  only Planning and/or Insurance (the servicing roles that asked), off
 *  for everyone else. An explicit choice, made from the notes dock's own
 *  link, always wins. Display only; nothing stored about a note changes. */
export const NOTES_LATEST_AT_BOTTOM: UserSettingDescriptor<boolean | null> = {
  id: "notes.latestAtBottom",
  baseline: () => null,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};

/** Health Information lists (Providers, Medications): show A–Z by name
 *  instead of the order they were entered (Ryan, 2026-09-28: a client on
 *  fifteen medications is read top to bottom against a pharmacy list, and
 *  entry order made that a hunt). One setting per list, so sorting one
 *  doesn't reorder the other. Display only — nothing about the stored
 *  order changes, so there is nothing to undo. Account-level: someone who
 *  wants their lists alphabetical wants that on every computer. */
export const HEALTH_PROVIDERS_SORT_AZ: UserSettingDescriptor<boolean> = {
  id: "health.providersSortAz",
  baseline: () => false,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};
export const HEALTH_MEDICATIONS_SORT_AZ: UserSettingDescriptor<boolean> = {
  id: "health.medicationsSortAz",
  baseline: () => false,
  parse: (raw) => (typeof raw === "boolean" ? raw : null),
  serialize: (value) => value,
};

/** "Connect your email & calendar" nudge — dismissing it on one browser
 *  and being nagged again on the next is exactly the annoyance this
 *  program exists to remove. */
export const CONNECT_EMAIL_BANNER_DISMISSED = booleanSetting({
  id: "email.connectBannerDismissed",
  baseline: false,
  legacyKey: "connect-email-banner-dismissed-v1",
});

/** Email: which mailbox you're working in (your own, or a shared /
 *  delegate one). Follows the account so someone servicing a shared
 *  mailbox stays in it when they move machines. null = own mailbox. */
export const EMAIL_SELECTED_MAILBOX: UserSettingDescriptor<string | null> = {
  id: "email.selectedMailbox",
  baseline: () => null,
  parse: (raw) => (typeof raw === "string" && raw.length > 0 ? raw : null),
  serialize: (value) => value,
  legacyKey: "stewardship-os.email.selected-mailbox.v1",
  parseLegacy: (raw) => (raw.length > 0 ? raw : null),
};

/** Email: conversation-grouped vs one row per message. */
// ─── E-sign send defaults ─────────────────────────────────────────────
//
// The Prepare & Send screen's envelope clock — how often to chase an
// unsigned document, how many times, and whether it expires — started
// as fixed per-send defaults (follow up every 3 days, up to 3 times, no
// deadline). The E-sign → Settings screen (2026-08-26) makes them the
// USER'S defaults: what Prepare & Send opens with, still adjustable on
// every individual send. Stored as the select-option strings the send
// screen renders, so the settings card and the per-send control can
// share one vocabulary. Born account-level — no legacy browser key.

const ESIGN_REMINDER_VALUES = ["off", "2", "3", "7", "14"] as const;
export type EsignReminderChoice = (typeof ESIGN_REMINDER_VALUES)[number];
export const ESIGN_DEFAULT_REMINDER: UserSettingDescriptor<EsignReminderChoice> = {
  id: "esign.defaultReminder",
  baseline: () => "3",
  parse: (raw) =>
    typeof raw === "string" && (ESIGN_REMINDER_VALUES as readonly string[]).includes(raw)
      ? (raw as EsignReminderChoice)
      : null,
  serialize: (value) => value,
};

const ESIGN_REMINDER_MAX_VALUES = ["1", "2", "3", "5"] as const;
export type EsignReminderMaxChoice = (typeof ESIGN_REMINDER_MAX_VALUES)[number];
export const ESIGN_DEFAULT_REMINDER_MAX: UserSettingDescriptor<EsignReminderMaxChoice> = {
  id: "esign.defaultReminderMax",
  baseline: () => "3",
  parse: (raw) =>
    typeof raw === "string" && (ESIGN_REMINDER_MAX_VALUES as readonly string[]).includes(raw)
      ? (raw as EsignReminderMaxChoice)
      : null,
  serialize: (value) => value,
};

const ESIGN_EXPIRY_VALUES = ["none", "7", "14", "30", "90"] as const;
export type EsignExpiryChoice = (typeof ESIGN_EXPIRY_VALUES)[number];
export const ESIGN_DEFAULT_EXPIRY: UserSettingDescriptor<EsignExpiryChoice> = {
  id: "esign.defaultExpiry",
  baseline: () => "none",
  parse: (raw) =>
    typeof raw === "string" && (ESIGN_EXPIRY_VALUES as readonly string[]).includes(raw)
      ? (raw as EsignExpiryChoice)
      : null,
  serialize: (value) => value,
};

// ── E-sign: my own starting wording, and videos to reuse (PR 8f) ──
// "Save this wording as my default for SPDS sends" and "Remember this
// video for the next SPDS I send on this listing", from the "Before it
// goes" drawing. Both are the sender's own: they shape what THEIR next
// send starts with, never what anyone else sends (per-user tailoring
// under "Every user gets their own view"). Account-level, so they follow
// the sender to any computer.

/** Key for a send whose first document has no type. */
export const ESIGN_ANY_DOCUMENT_TYPE = "*";
const MAX_WORDING_TYPES = 40;
const MAX_REMEMBERED_VIDEOS = 30;

export interface EsignWordingDefault {
  /** The note, as the sender wrote it. */
  note: string;
  /** The email's lines with tokens, or null for the library's wording. */
  body: string | null;
}

export const ESIGN_WORDING_DEFAULTS: UserSettingDescriptor<Record<string, EsignWordingDefault>> = {
  id: "esign.wordingDefaults",
  baseline: () => ({}),
  parse: (raw) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const out: Record<string, EsignWordingDefault> = {};
    for (const [type, v] of Object.entries(raw as Record<string, unknown>).slice(0, MAX_WORDING_TYPES)) {
      if (!v || typeof v !== "object") continue;
      const w = v as { note?: unknown; body?: unknown };
      if (typeof w.note !== "string") continue;
      out[type] = {
        note: w.note.slice(0, 2000),
        body: typeof w.body === "string" ? w.body.slice(0, 4000) : null,
      };
    }
    return out;
  },
  serialize: (value) => value,
};

export interface EsignRememberedVideo {
  /** "opportunity:{id}" or "transaction:{id}". */
  record: string;
  documentType: string;
  video: { url: string; title: string | null; durationSeconds: number | null; thumbnailUrl: string | null };
  savedAt: string;
}

export const ESIGN_REMEMBERED_VIDEOS: UserSettingDescriptor<EsignRememberedVideo[]> = {
  id: "esign.rememberedVideos",
  baseline: () => [],
  parse: (raw) => {
    if (!Array.isArray(raw)) return null;
    const out: EsignRememberedVideo[] = [];
    for (const v of raw) {
      if (!v || typeof v !== "object") continue;
      const r = v as Partial<EsignRememberedVideo>;
      const video = r.video as Partial<EsignRememberedVideo["video"]> | undefined;
      if (typeof r.record !== "string" || typeof r.documentType !== "string") continue;
      if (!video || typeof video.url !== "string") continue;
      out.push({
        record: r.record,
        documentType: r.documentType,
        video: {
          url: video.url,
          title: typeof video.title === "string" ? video.title : null,
          durationSeconds: typeof video.durationSeconds === "number" ? video.durationSeconds : null,
          thumbnailUrl: typeof video.thumbnailUrl === "string" ? video.thumbnailUrl : null,
        },
        savedAt: typeof r.savedAt === "string" ? r.savedAt : "",
      });
    }
    return out.slice(0, MAX_REMEMBERED_VIDEOS);
  },
  serialize: (value) => value,
};

/** The list with this record and type's video set (or cleared), newest
 *  first and capped, so it never grows without bound. */
export function rememberVideo(
  list: readonly EsignRememberedVideo[],
  entry: Omit<EsignRememberedVideo, "savedAt" | "video"> & { video: EsignRememberedVideo["video"] | null },
  now: string,
): EsignRememberedVideo[] {
  const rest = list.filter((v) => !(v.record === entry.record && v.documentType === entry.documentType));
  if (!entry.video) return rest;
  return [{ ...entry, video: entry.video, savedAt: now }, ...rest].slice(0, MAX_REMEMBERED_VIDEOS);
}

export const EMAIL_GROUPING_MODE = enumSetting({
  id: "email.groupingMode",
  values: ["thread", "flat"] as const,
  baseline: "thread",
  legacyKey: "stewardship.email.messageGroupingMode.v1",
});

// Which Microsoft To-Do lists feed a dashboard's live "Tasks Due" tile
// (Brian Ream, 2026-08-28: his tile only showed the narrow
// Stewardship-linked list and he wanted "the appropriate tasks").
// `null` (the baseline — nothing saved yet) means "no filter, show every
// list" — the same behavior a fresh account gets before ever opening the
// picker. An explicit `[]` means the advisor unchecked every list, which
// is a deliberate "show nothing," distinct from `null`. Born
// account-level — there is no prior localStorage key to adopt.
export const DASHBOARD_TODO_LIST_FILTER: UserSettingDescriptor<string[] | null> = {
  id: "dashboard.todoListFilter",
  baseline: () => null,
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  serialize: (value) => value,
};

// The Financial dashboard's "Funds watch" ticker list (2026-09-04 D1
// rebuild) — every fund an advisor tracks, editable in place ("edit
// list"), account-level so it follows Brian or Jake to a second
// computer instead of resetting to the shared default. Mirrors Brian's
// original 2026-06-17 list (shared/market-data/brian-funds.ts) as the
// baseline for a first-time user; this module is pure and imports
// nothing, so the default is a literal copy rather than an import.
const FUNDS_WATCH_DEFAULT_TICKERS: readonly string[] = [
  "RSSB",
  "DFAX",
  "CTA",
  "RDVY",
  "QDSIX",
  "SDVY",
  "AAEQ",
  "CAOS",
  "DFAW",
  "BUFR",
  "RSSX",
];

/** Insurance view of the household page (Brandon Ream, 2026-09-29):
 *  which folded sections under the household summary this person keeps
 *  open. Brandon asked for everything folded ("I don't want anything
 *  else to stay open"), so the baseline is none; opening one is
 *  remembered on the account, so it stays open on every household and
 *  every computer until they close it. Ids are InsuranceFoldId values. */
export const HOUSEHOLD_INSURANCE_OPEN_FOLDS: UserSettingDescriptor<string[]> = {
  id: "household.insuranceOpenFolds",
  baseline: () => [],
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  serialize: (value) => value,
};

/** The Insurance view of an insurance POLICY page (Brandon, 2026-09-29):
 *  which folded rows (carrier data, documents, owner and status controls,
 *  history...) this person keeps open. Separate from the household page's
 *  list, since the rows differ. Baseline: everything folded. */
export const POLICY_INSURANCE_OPEN_FOLDS: UserSettingDescriptor<string[]> = {
  id: "policy.insuranceOpenFolds",
  baseline: () => [],
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  serialize: (value) => value,
};

export const FUNDS_WATCH_TICKERS: UserSettingDescriptor<string[]> = {
  id: "dashboard.fundsWatchTickers",
  baseline: () => [...FUNDS_WATCH_DEFAULT_TICKERS],
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  serialize: (value) => value,
};

// ─── Household: address heads-ups this person has hidden ──────────────

// "Hide for me" on the household page's "Heads up: other households at
// <address>" strip. Each entry is `<household id>|<address label>`, so the
// strip returns by itself if the household moves, and hiding it on one
// household never hides it on another. Per person on purpose: it is a
// reminder Brandon and Joy asked for, so one advisor tuning it out must
// not change what a colleague sees. Born account-level, no legacyKey.
const MAX_HIDDEN_ADDRESS_HEADSUPS = 500;

export const HOUSEHOLD_HIDDEN_ADDRESS_HEADSUPS: UserSettingDescriptor<string[]> = {
  id: "household.hiddenAddressHeadsUps",
  baseline: () => [],
  parse: (raw) => (Array.isArray(raw) ? asStringArray(raw) : null),
  // Keep the newest entries if it ever grows past the cap.
  serialize: (value) => value.slice(-MAX_HIDDEN_ADDRESS_HEADSUPS),
};

// ─── John Hancock 401(k) file: which Gusto column fills which money type ─
//
// The John Hancock page (commissions build program 9.5) asks once which of
// John Hancock's money types (EEDEF, EEROT, SHMAC...) each Gusto 401(k)
// column fills, and remembers the answer so the next payroll needs no
// clicks. Keyed by the Gusto column's header, lower case. Holds headers and
// codes only: never a name, an amount or anything else from a file.
// Account-level (the same choice on every computer).

const MAX_RETIREMENT_COLUMNS = 60;

export const RETIREMENT_COLUMN_CHOICES: UserSettingDescriptor<Record<string, string>> = {
  id: "retirement.johnHancockColumns",
  baseline: () => ({}),
  parse: (raw) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>).slice(0, MAX_RETIREMENT_COLUMNS)) {
      if (typeof v !== "string" || !/^[A-Z][A-Z0-9]{1,9}$/.test(v)) continue;
      out[k.slice(0, 120)] = v;
    }
    return out;
  },
  serialize: (value) => value,
};

// ─── The registry ─────────────────────────────────────────────────────

// Every descriptor the app knows about. The provider walks this once per
// boot to run the legacy-adoption sweep; nothing else needs the list.
//
// Typed as descriptors over `unknown` because the array is heterogeneous
// — each entry is internally consistent, and the provider only ever uses
// the erased operations (id / legacyKey / parseLegacy / serialize).
export const USER_SETTINGS_REGISTRY: ReadonlyArray<UserSettingDescriptor<never>> = [
  EMAIL_FOLDER_PREFS,
  EMAIL_FAVORITES,
  NAV_PINS,
  NAV_COLLAPSED_SECTIONS,
  NAV_SECOND_PANE_MODE,
  NAV_LAST_SECTION,
  NAV_ADMIN_GROUPS_MODE,
  APPEARANCE_DARK_MODE,
  SCOPE_COMPANY,
  SCOPE_BOOK,
  SCOPE_BOOK_DEFAULT,
  SCOPE_CLEANUP_OWNER,
  SCOPE_FORMS,
  TASKS_PREFERRED_TAB,
  ONGOING_CARE_VIEW,
  ONGOING_CARE_OPEN_TARGET,
  ONGOING_CARE_FILTERS,
  MARKETING_TODAY_VIEW,
  MARKETING_CALENDAR_VIEW,
  CALENDAR_VIEW,
  DONNA_SCHEDULE_VIEW,
  DONNA_SCHEDULE_GROUP,
  DONNA_BOARD_LAYOUT,
  DONNA_BOARD_RANGE,
  DONNA_EXTRA_ADVISORS,
  DONNA_BOARD_MEMBER_IDS,
  DONNA_VOICE_BRIEF,
  BOARD_AUTOMATIONS_OPEN,
  DENSITY_MY_DAY,
  DENSITY_PROCESSING_BOARD,
  DENSITY_ADMIN_TEAM,
  PROC_HERO_COLLAPSED,
  PROC_CONDITIONS_COMPLIANCE_HIDDEN,
  PROC_LOAN_SUMMARY_CREDITS_SLOT,
  PROC_NOTES_MEETINGS_POSITION,
  NOTES_LATEST_AT_BOTTOM,
  BRIEFING_MORE_OPEN,
  CONNECT_EMAIL_BANNER_DISMISSED,
  EMAIL_SELECTED_MAILBOX,
  EMAIL_GROUPING_MODE,
  ESIGN_DEFAULT_REMINDER,
  ESIGN_DEFAULT_REMINDER_MAX,
  ESIGN_DEFAULT_EXPIRY,
  DASHBOARD_TODO_LIST_FILTER,
  FUNDS_WATCH_TICKERS,
  HOUSEHOLD_INSURANCE_OPEN_FOLDS,
  POLICY_INSURANCE_OPEN_FOLDS,
  HOUSEHOLD_HIDDEN_ADDRESS_HEADSUPS,
] as unknown as ReadonlyArray<UserSettingDescriptor<never>>;

// ─── Blob helpers ─────────────────────────────────────────────────────

/** Decode a stored blob. Malformed → null (caller falls back to an empty
 *  blob, i.e. every setting on its baseline). */
export function parseUserSettingsBlob(json: string): UserSettingsBlob | null {
  const raw = parseJson(json);
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  return raw as UserSettingsBlob;
}

/** Read one setting out of a blob, falling back to its baseline when the
 *  slot is missing or malformed. */
export function readSetting<T>(blob: UserSettingsBlob, descriptor: UserSettingDescriptor<T>): T {
  if (!(descriptor.id in blob)) return descriptor.baseline();
  const parsed = descriptor.parse(blob[descriptor.id]);
  return parsed === null ? descriptor.baseline() : parsed;
}

/** Return a NEW blob with one setting replaced. Never mutates the input
 *  — the provider holds the blob in React state, and an in-place edit
 *  would skip the re-render that repaints the sidebar. */
export function writeSetting<T>(
  blob: UserSettingsBlob,
  descriptor: UserSettingDescriptor<T>,
  value: T,
): UserSettingsBlob {
  return { ...blob, [descriptor.id]: descriptor.serialize(value) };
}

/**
 * One-time adoption of settings that used to live in per-browser
 * storage. For each registry entry with a `legacyKey`: if the account
 * blob has no value yet AND this browser has one, take the browser's.
 *
 * This is what keeps the promise that nobody's setup resets to stock the
 * day their settings start syncing — the browser they already customized
 * seeds the account.
 *
 * The account always wins when it has an answer, so a stale copy sitting
 * on a browser someone used months ago can never overwrite what they've
 * since set elsewhere.
 *
 * The legacy keys are deliberately LEFT in place rather than cleaned up.
 * They cost a few bytes and they're the escape hatch if this ships
 * broken — a rollback still finds each user's setup where the old code
 * expects it.
 *
 * Pure: takes a reader function rather than touching localStorage, so it
 * tests without a DOM.
 */
export function adoptLegacySettings(
  blob: UserSettingsBlob,
  readLegacy: (key: string) => string | null,
  listLegacyKeys: () => string[] = () => [],
): UserSettingsBlob {
  let next = blob;
  for (const descriptor of USER_SETTINGS_REGISTRY) {
    const d = descriptor as unknown as UserSettingDescriptor<unknown>;
    if (d.id in next) continue; // the account already has an answer
    let parsed: unknown = null;
    try {
      if (d.adoptLegacy) {
        parsed = d.adoptLegacy(readLegacy, listLegacyKeys);
      } else if (d.legacyKey && d.parseLegacy) {
        const raw = readLegacy(d.legacyKey);
        parsed = raw === null ? null : d.parseLegacy(raw);
      }
    } catch {
      // A browser that throws on storage access (private mode) must not
      // be able to take down app boot.
      continue;
    }
    if (parsed === null) continue;
    next = writeSetting(next, d, parsed);
  }
  return next;
}
